'use client';

import React, { useMemo } from 'react';
import type { Chat } from '../../types';
import type { UIMessage } from 'ai';
import type { FilePart } from '../../lib/db/types';
import type { AttachedSkill } from '../skills/types';
import { ChatInterface } from '../ChatInterface';
import type { ChatRunStatus } from '../../contexts/ChatActivityContext';
import { ChatActions } from '../ChatActions';
import { useChatHeaderActions } from '../../contexts/ChatHeaderActionsContext';
import { CHAT_LAYOUT, HEADER, WRAPPER } from '../../styles/chat-layout';

interface ChatActiveChatStateProps {
  selectedChatId: string;
  selectedChat: Chat;
  apiBaseUrl: string;
  locale: string;
  initialQuery: string;
  /** Files attached on the start screen; ride along with the first message. */
  initialFiles?: FilePart[];
  /** Skill attached on the start screen; applied from the first message on. */
  initialSkill?: AttachedSkill | null;
  hasMoreMessages: boolean;
  isLoadingOlderMessages: boolean;
  messages: UIMessage[];
  onMessageSent: () => void;
  onLoadOlderMessages: () => void;
  onShare: () => void;
  onArchive: () => void;
  onDelete: () => void;
  botAvatarUrl?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  meridianLogoUrl?: string;
  mcpConnectionsHref?: string;
  /** Reports this chat's run status upward (tab dots / sidebar dots). */
  onStatusChange?: (status: ChatRunStatus) => void;
}

/**
 * Active chat view component
 * Displays chat header with title and actions, plus the chat interface
 * 
 * Uses UIMessage[] directly from API (includes tool-call/tool-result parts)
 * 
 * OPTIMIZATION: Memoizes props to prevent unnecessary re-renders of ChatInterface
 * when parent re-renders but props haven't changed
 */
export function ChatActiveChatState({
  selectedChatId,
  selectedChat,
  apiBaseUrl,
  locale,
  initialQuery,
  initialFiles,
  initialSkill,
  hasMoreMessages,
  isLoadingOlderMessages,
  messages,
  onMessageSent,
  onLoadOlderMessages,
  onShare,
  onArchive,
  onDelete,
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  meridianLogoUrl = '/meridian.png',
  mcpConnectionsHref,
  onStatusChange,
}: ChatActiveChatStateProps) {
  // Messages are already in UIMessage[] format from API, no conversion needed
  // This preserves tool-call and tool-result parts

  // Memoize ChatInterface props to prevent unnecessary re-renders
  const chatInterfaceProps = useMemo(() => ({
    chatId: selectedChatId,
    chatTitle: selectedChat.title,
    apiBaseUrl,
    onMessageSent,
    initialQuery,
    initialFiles,
    initialSkill,
    showSuggestedMessages: true,
    locale,
    onLoadOlderMessages: onLoadOlderMessages ? async () => { await onLoadOlderMessages(); } : undefined,
    hasMoreMessages,
    isLoadingOlderMessages,
    initialMessages: messages,
    botAvatarUrl,
    userInitials,
    userAvatarUrl,
    mcpConnectionsHref,
    onStatusChange,
  }), [
    selectedChatId,
    selectedChat.title,
    apiBaseUrl,
    onMessageSent,
    initialQuery,
    initialFiles,
    initialSkill,
    locale,
    onLoadOlderMessages,
    hasMoreMessages,
    isLoadingOlderMessages,
    messages,
    botAvatarUrl,
    userInitials,
    userAvatarUrl,
    mcpConnectionsHref,
    onStatusChange,
  ]);

  const renderHeaderActions = useChatHeaderActions();

  return (
    <div className={WRAPPER.chatActiveChatState}>
      {/* Chat Header */}
      <div className={HEADER.chatHeader}>
        {/* min-w-0 lets the title flex item shrink so `truncate` engages instead
            of the title pushing ChatActions off-screen on narrow viewports. */}
        <div className="flex items-center gap-3 min-w-0">
          <img src={meridianLogoUrl} alt="Meridian" className={HEADER.headerIcon} />
          <h1 className={HEADER.chatTitle}>{selectedChat.title}</h1>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {/* Host-supplied actions (Hub contributes transcript export, which
              needs its own Slack channel picker) render before the generic
              archive/delete pair. */}
          {renderHeaderActions?.({
            chatId: selectedChatId,
            title: selectedChat.title ?? '',
            status: selectedChat.status,
          })}
          <ChatActions onShare={onShare} onArchive={onArchive} onDelete={onDelete} status={selectedChat.status} />
        </div>
      </div>

      {/* Chat Interface - key by chatId so each chat gets a fresh instance and initial-query effect runs once per chat */}
      <ChatInterface key={selectedChatId} {...chatInterfaceProps} />
    </div>
  );
}
